import { StackedBarChartData } from './model/stacked-bar-chart';
import { Component, OnInit, ViewChild, Input, ElementRef, OnChanges, Renderer2, OnDestroy } from '@angular/core';
import * as d3 from 'd3';
import { Subscription } from 'rxjs';
import { TranslationService } from '@sdbn/common';



@Component({
    selector: 'app-stacked-bar-chart',
    templateUrl: './stackedbarchart.component.html',
    styleUrls: ['./stackedbarchart.component.scss'],
    standalone: false
})
export class StackedBarchartComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('chart', { static: true }) private chartContainer: ElementRef;
  @ViewChild('legend', { static: true }) private legendContainer: ElementRef;

  /*
      {'chart':{
        'stackDefinitions':[
          {'key':'FINISHED','label':'Abgearbeitet','color':'#32a852'},
          {'key':'IN_WORK','label':'In Bearbeitung','color':'#ff8438'},
          {'key':'PLANNED','label':'Geplant','color':'#3269a8'}
        ],
        'bars':[
          {'label':'Station 1','parts':{'FINISHED':30,'IN_WORK':10,'PLANNED':50},'total':90},
          {'label':'Station 2','parts':{'FINISHED':5,'IN_WORK':3,'PLANNED':20},'total':28},
          {'label':'Station 3','parts':{'FINISHED':13,'IN_WORK':4,'PLANNED':37},'total':54}
        ]
      }}
   */
  @Input('data') chartData: StackedBarChartData;

  private barHeight = 50;
  private barPadding = 0.08;
  private margin: any = { top: 20, bottom: 20, left: 150, right: 20 };
  private width: number;
  private height: number;
  private xAxis: any;
  private yAxis: any;

  private languageSubscription: Subscription;

  constructor(private renderer: Renderer2, private translationService: TranslationService) {
    this.languageSubscription = translationService.changed.subscribe(language => {

      this.clearContent(this.chartContainer.nativeElement);
      this.clearContent(this.legendContainer.nativeElement);

      this.createChart();

    });

  }

  ngOnDestroy() {
    if (this.languageSubscription != null) {
      this.languageSubscription.unsubscribe();
    }
  }

  ngOnInit() {

  }

  ngOnChanges(changes: import("@angular/core").SimpleChanges): void {
    this.clearContent(this.chartContainer.nativeElement);
    this.clearContent(this.legendContainer.nativeElement);

    this.createChart();
  }

  createChart() {
    // Exctract the keys the stacked bar is build on
    const keys = new Array<string>();
    this.chartData.stackDefinitions.forEach(elm => {
      keys.push(elm.key);
    });
    // We transform our model to the form we need for d3 stacked diagrams.
    const values: Array<any> = this.chartData.bars.map(bar => {
      const r = {
        label: bar.label
      };
      for (const key of Object.keys(bar.parts)) {
        r[key] = bar.parts[key];
      }
      return r;
    });
    const stack = d3.stack().keys(keys);
    const stackedSeries = stack(values);

    const element = this.chartContainer.nativeElement;
    // Calculate width and heigth based on surrounding container
    const bounds = element.getBoundingClientRect();
    const clientWidth = bounds.width;

    this.width = clientWidth - this.margin.left - this.margin.right;
    this.height = values.length * (this.barHeight + this.barPadding);

    const x = d3.scaleLinear()
      .domain([0, d3.max(stackedSeries, d => d3.max(d, d => d[1]))])
      .range([this.margin.left, this.width - this.margin.right]);

    const y = d3.scaleBand()
      .domain(values.map(d => d.label))
      .range([this.margin.top, this.height - this.margin.bottom])
      .padding(this.barPadding);

    this.xAxis = g => g
      .attr('transform', `translate(0,${this.margin.top})`)
      .call(d3.axisTop(x).ticks(this.width / 100, 's'))
      .call(g => g.selectAll('.domain').remove());

    this.yAxis = g => g
      .attr('transform', `translate(${this.margin.left},0)`)
      .call(d3.axisLeft(y).tickSizeOuter(0))
      .call(g => g.selectAll('.domain').remove());

    const svg = d3.select(element).append('svg')
      .attr('width', clientWidth + 'px')
      .attr('height', this.height + 'px');

    const frame = svg.append('g')
      .selectAll('g')
      .data(stackedSeries)
      .join('g')
      .attr('fill', d => this.color(d));

    // Our bars
    frame.selectAll('g')
      .attr('class', 'bar')
      .data(d => d)
      .join('g')
      .attr('class', 'content')
      .append('rect')
      .attr('x', d => x(d[0]))
      .attr('y', (d, i) => y(d.data.label + ''))
      .attr('width', d => x(d[1]) - x(d[0]))
      .attr('height', y.bandwidth());
    frame.selectAll('.content')
      .data(d => {
        // Dirty hack: We copy the key in data row so we can calculate the text color.
        // No idea, why this key is getting lost between stacked bar generation and text creation.
        for (const i of Object.keys(d)) {
          if (i !== 'index' && i !== 'key') {
            d[i].key = d.key;
          }
        }
        return d;
      })
      .append('text')
      // start of text is half width - 10 px
      .attr('x', d => x(d[0]) - 5 + (x(d[1]) - x(d[0])) / 2)
      // 6 = half of font size to center text
      .attr('y', (d, i) => y(d.data.label + '') + 6 + y.bandwidth() / 2)
      .attr('height', y.bandwidth())
      .style('text-anchor', 'start')
      .style('font-size', 12)
      .style('fill', d => this.colorForText(d))
      .text(d => {
        const value = d[1] - d[0];
        return value !== 0 ? value : '';
      });

    svg.append('g').call(this.xAxis);

    svg.append('g').call(this.yAxis);

    // create legend
    const legendData = this.chartData.stackDefinitions;
    const legend = d3.select(this.legendContainer.nativeElement).append('svg')
      .attr('width', '100%')
      .append('g')
      .attr('transform', `translate(${this.margin.left},0)`)
      .selectAll('g')
      .data(legendData)
      .enter()
      .append('g');

    legend.append('rect')
      .attr('fill', (d, i) => d.color)     //   const color = d3.scaleOrdinal(d3.schemeCategory10);
      .attr('height', 15)
      .attr('width', 15);

    legend.append('text')
      .attr('x', 18)
      .attr('y', 10)
      .attr('dy', '.15em')
      // translate
      .text((d, i) => {
        const i18n = this.translationService.translate(d.dictionaryKey);
        return i18n == null ? d.label : i18n;
      })
      .style('text-anchor', 'start')
      .style('font-size', 12);

    // Positionieren der einzelnen LegendEntries
    const padding = 10;
    legend.attr('transform', (d, i) => {
      return 'translate(' + (d3.sum(legendData, (e, j) => {
        if (j < i) {
          return legend.nodes()[j].getBBox().width;
        }
        return 0;
      }) + padding * i) + ',0)';
    });
  }

  /** Helper to get right color for a stack definition. */
  private color(d: any) {
    for (const entry of this.chartData.stackDefinitions) {
      if (entry.key === d.key) {
        return entry.color;
      }
    }
    return '#cce7e8';
  }

  private colorForText(d: any) {
    for (const entry of this.chartData.stackDefinitions) {
      if (entry.key === d.key) {
        return entry.textColorBar;
      }
    }
    return '#0d0d0d';
  }

  /** Helper to remove all child nodes of a given element in DOM.  */
  private clearContent(element: any) {
    const childElements = element.childNodes;
    for (const child of childElements) {
      this.renderer.removeChild(element, child);
    }
  }
}
